import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { Card, Col, Form, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import ReactHtmlParser from 'react-html-parser';
import { useTranslation } from 'react-i18next';
import { BiCheck, BiSupport } from 'react-icons/bi';
import { MdOutlineQuestionAnswer } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { PSButton } from '@/components/ui/button';

import { useAppSelector } from '../../app/hook';
import { useGetProfileQuery } from '../../app/services/auth-rtk.service';
import { useCreateContactUsMutation } from '../../app/services/chatbot-rtk.services';
import { AlertsType } from '../../constants/alerts.constant';
import { MultiLanguageUrl } from '../../constants/multiLanguage.constant';
import { selectUserProfile } from '../../features/auth/auth.slice';
import { WrappedErrorBoundary } from '../WrappedErrorBoundary';

const schema = Yup.object({
  category: Yup.string().required().label('Category'),
  message: Yup.string().required().min(10).max(3500).required().label('Message'),
});

const ContactUs = () => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      category: '',
      message: '',
    },
    resolver: yupResolver(schema),
  });

  const { i18n } = useTranslation();
  const navigation = useNavigate();
  const [counter, setCounter] = useState(0);

  const [getProfile, setGetProfile] = useState(true);

  const profileRequest = useGetProfileQuery(undefined, {
    skip: getProfile,
  });

  const profile = useAppSelector(selectUserProfile);

  const [ContactUsRequest, ContactUsRequestStatus] = useCreateContactUsMutation();

  useEffect(() => {
    if (!profile || !profile.user_email) {
      setGetProfile(false);
    }
  }, [profile]);

  const onSubmit = (values: any) => {
    let firstName: string | undefined = '';
    if (profile?.user_first_name && profile.user_last_name) {
      firstName = profile?.user_first_name + ' ' + profile.user_last_name;
    } else {
      firstName = profile?.user_email;
    }

    if (counter <= 0) {
      // Concatenate category and message to maintain API interface
      const combinedMessage = `Topic: [${t(`DashboardContactUs/category-${values.category}`)}] - User message: ${values.message}`;

      ContactUsRequest({
        email: profile?.user_email as string,
        message: combinedMessage,
      })
        .unwrap()
        .then(res => {
          if (res && res.message) {
            setCounter(counter + 1);
            toast.success(res.message);
            reset();
            navigation(`/${MultiLanguageUrl[i18n.language] || 'en-US'}/control-panel`);
          }
        })
        .catch(res => {
          if (res) {
            toast.error(t(AlertsType.ISCUKO001));
          }
          reset();
        });
    } else {
      toast.error(t(AlertsType.ISCUKO002));
    }
  };

  return (
    <div className="mt-0">
      {/* Header */}
      <Row className="py-2 px-1 border-bottom align-items-center m-0">
        <Col lg="9" sm="9" xs="9" className="p-0">
          <h3 className="m-0 tw-ps-5 d-flex align-items-center gap-2">
            <BiSupport className="tw-text-primary" size={24} />
            {t('DashboardContactUs/title')}
          </h3>
        </Col>
      </Row>

      {/* Main Content */}
      <div className="tw-p-6">
        <Row className="justify-content-center">
          <Col lg={8} md={10} xs={12}>
            <Card className="tw-shadow-lg tw-border-0 bg-transparent">
              <Card.Body className="tw-p-8 bg-transparent">
                {/* Welcome Section */}
                <div className="text-center tw-mb-8">
                  <div className="tw-mb-4">
                    <MdOutlineQuestionAnswer className="tw-text-primary tw-mx-auto" size={48} />
                  </div>
                  <h2 className="font-sofiaSans tw-text-2xl tw-font-semibold tw-text-gray-800 tw-mb-3">{t('DashboardContactUs/welcome-title')}</h2>
                  <p className="tw-text-gray-600 tw-text-lg tw-leading-relaxed">{t('DashboardContactUs/welcome-subtitle')}</p>
                </div>

                {/* Contact Form */}
                <Form onSubmit={handleSubmit(onSubmit)}>
                  <Row>
                    <Col>
                      <div className="tw-mb-6">
                        <Form.Group className="tw-mb-4">
                          <Form.Label className="tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
                            {t('DashboardContactUs/category-label')}
                            <span className="tw-text-red-500 tw-ml-1">*</span>
                          </Form.Label>
                          <Form.Select
                            isInvalid={!!errors.category}
                            className="tw-border-gray-300 tw-rounded-lg tw-text-sm focus:tw-border-primary focus:tw-ring-2 focus:tw-ring-primary/20"
                            {...register('category')}
                          >
                            <option value="">{t('DashboardContactUs/category-placeholder')}</option>
                            <option value="technical-support">{t('DashboardContactUs/category-technical-support')}</option>
                            <option value="feature-request">{t('DashboardContactUs/category-feature-request')}</option>
                            <option value="marketplace">{t('DashboardContactUs/category-marketplace')}</option>
                            <option value="general">{t('DashboardContactUs/category-general')}</option>
                            <option value="other">{t('DashboardContactUs/category-other')}</option>
                          </Form.Select>
                          <Form.Control.Feedback type="invalid" className="tw-text-sm">
                            {errors.category?.message}
                          </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group>
                          <Form.Label className="tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
                            {t('DashboardContactUs/l-01')}
                            <span className="tw-text-red-500 tw-ml-1">*</span>
                          </Form.Label>
                          <Form.Control
                            isInvalid={!!errors.message}
                            id="message"
                            as="textarea"
                            rows={6}
                            className="tw-border-gray-300 tw-rounded-lg tw-text-sm focus:tw-border-primary focus:tw-ring-2 focus:tw-ring-primary/20"
                            placeholder={t('DashboardContactUs/message-placeholder')}
                            {...register('message')}
                          />
                          <Form.Control.Feedback type="invalid" className="tw-text-sm">
                            {errors.message?.message}
                          </Form.Control.Feedback>
                          <div className="tw-text-xs tw-text-gray-500 tw-mt-2">{t('DashboardContactUs/message-hint')}</div>
                        </Form.Group>
                      </div>
                    </Col>
                  </Row>

                  {/* Submit Button */}
                  <div className="tw-flex tw-flex-col tw-items-end tw-pt-4 tw-border-t tw-border-gray-100">
                    <PSButton
                      id="btn-contact-submit"
                      variant="primary"
                      type="submit"
                      title={ContactUsRequestStatus.isLoading ? t('DashboardContactUs/sending') : t('DashboardContactUs/btn-01')}
                      disabled={ContactUsRequestStatus.isLoading}
                      className="tw-px-6 tw-py-2 tw-mb-3"
                    ></PSButton>
                    <div className="tw-text-sm tw-text-gray-500 tw-flex tw-items-center tw-gap-1">
                      <BiCheck size={16} />
                      {ReactHtmlParser(t('DashboardContactUs/response-time'))}
                    </div>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default WrappedErrorBoundary(ContactUs);
